import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Medication as MedIcon,
  LocalHospital as HospitalIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Description as SoapIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assignment as ReportIcon,
} from '@mui/icons-material';
import { reportKindLabel, openFile as openReportFile } from './Reports';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function VisitDetail() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareAnchor, setShareAnchor] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [soapOpen, setSoapOpen] = useState(false);
  const [visitReports, setVisitReports] = useState([]);

  useEffect(() => {
    fetchVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId]);

  const fetchVisit = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/patient/summaries/${visitId}`);
      setVisit(response.data);
      // Reports linked to this visit — by the clinic or by the patient. Best-effort:
      // the summary stands on its own if this fails.
      axios.get(`${API_URL}/api/v1/reports/mine`)
        .then((r) => setVisitReports((r.data.reports || []).filter((x) => x.session_id === visitId)))
        .catch(() => setVisitReports([]));
    } catch {
      toast.error('Failed to load visit details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const buildPlainSummary = () => {
    if (!visit) return '';
    const lines = [
      `Visit summary — ${formatDate(visit.date)}`,
      `Doctor: ${visit.doctor_name}`,
    ];
    if (visit.clinic_name) lines.push(`Clinic: ${visit.clinic_name}`);
    if (visit.reason) lines.push(`Reason: ${visit.reason}`);
    if (visit.summary) lines.push('', 'What we discussed:', visit.summary);
    if (visit.follow_up_date) lines.push('', `Follow-up due: ${visit.follow_up_date}`);
    if (visit.follow_up_instructions) lines.push('', 'Next steps:', visit.follow_up_instructions);
    if (visit.prescriptions?.length) {
      lines.push('', 'Prescriptions:');
      visit.prescriptions.forEach((rx) => {
        lines.push(`• ${rx.medication}${rx.sig ? ' — ' + rx.sig : ''}`);
      });
    }
    if (visit.medication_reminders?.length) {
      lines.push('', 'Medication reminders:');
      visit.medication_reminders.forEach((m) => lines.push(`• ${m}`));
    }
    return lines.join('\n');
  };

  const handleDownloadPdf = () => {
    setShareAnchor(null);
    window.print();
  };

  const handleShareWhatsApp = () => {
    setShareAnchor(null);
    const text = buildPlainSummary();
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleEmailClick = () => {
    setShareAnchor(null);
    setEmailDialogOpen(true);
  };

  const handleEmailSend = async () => {
    if (!emailRecipient) return;
    setSendingEmail(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/v1/patient/visits/${visitId}/share/email`,
        { to_email: emailRecipient },
      );
      if (res.data?.sent) {
        toast.success(`Visit summary sent to ${emailRecipient}`);
      } else {
        toast(`Email queued — SMTP may not be configured. Check server logs.`, { icon: '!' });
      }
      setEmailDialogOpen(false);
      setEmailRecipient('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography>Loading visit details...</Typography>
      </Box>
    );
  }

  if (!visit) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="error">Visit not found</Typography>
        <Button onClick={() => navigate('/')}>Back to Visits</Button>
      </Box>
    );
  }

  return (
    <Box className="visit-detail-print" sx={{
      '@media print': {
        '& .no-print': { display: 'none !important' },
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }} className="no-print">
        <IconButton onClick={() => navigate('/')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600} sx={{ flex: 1 }}>
          Visit Summary
        </Typography>
        <Button
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={(e) => setShareAnchor(e.currentTarget)}
        >
          Share
        </Button>
        <Menu
          anchorEl={shareAnchor}
          open={Boolean(shareAnchor)}
          onClose={() => setShareAnchor(null)}
        >
          <MenuItem onClick={handleDownloadPdf}>
            <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
            Download as PDF
          </MenuItem>
          <MenuItem onClick={handleEmailClick}>
            <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
            Send via email
          </MenuItem>
          <MenuItem onClick={handleShareWhatsApp}>
            <ListItemIcon><WhatsAppIcon fontSize="small" /></ListItemIcon>
            Share on WhatsApp
          </MenuItem>
        </Menu>
      </Box>

      {/* Visit Info Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
              <HospitalIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {formatDate(visit.date)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                with {visit.doctor_name}
                {visit.clinic_name ? ` · ${visit.clinic_name}` : ''}
              </Typography>
            </Box>
          </Box>
          {visit.reason && (
            <Chip
              label={visit.reason}
              sx={{ mt: 2, bgcolor: 'white', color: 'primary.main', fontWeight: 500 }}
            />
          )}
        </CardContent>
      </Card>

      {/* Follow-up Card */}
      {(visit.follow_up_instructions || visit.follow_up_date) && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<InfoIcon color="primary" />}
            title="Next Steps"
            titleTypographyProps={{ fontWeight: 600 }}
          />
          <CardContent>
            {visit.follow_up_date && (
              <Box sx={{ mb: visit.follow_up_instructions ? 2 : 0, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary">Your follow-up visit is due</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {new Date(`${visit.follow_up_date}T00:00:00Z`).toLocaleDateString(undefined, {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We'll email you a reminder before then. If you haven't booked it yet, contact the clinic or request an appointment under Find Care.
                </Typography>
              </Box>
            )}
            {visit.follow_up_instructions && (
              <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                {visit.follow_up_instructions}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reports from this visit */}
      {visitReports.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<ReportIcon color="primary" />}
            title="Reports from this visit"
            titleTypographyProps={{ fontWeight: 600 }}
          />
          <CardContent sx={{ pt: 0 }}>
            <List dense disablePadding>
              {visitReports.map((r) => (
                <ListItem key={r.id} disableGutters
                  secondaryAction={(
                    <Button size="small" onClick={() => openReportFile(`${API_URL}/api/v1/reports/mine/${r.id}/file`)}>
                      Open
                    </Button>
                  )}>
                  <ListItemText
                    primary={r.title}
                    secondary={[reportKindLabel(r),
                      r.uploaded_by_role === 'patient' ? 'added by you' : `added by ${r.uploaded_by_name || 'your clinic'}`].join(' · ')}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Prescriptions Card */}
      {visit.prescriptions && visit.prescriptions.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<MedIcon color="primary" />}
            title="Prescriptions"
            titleTypographyProps={{ fontWeight: 600 }}
            subheader="As entered by your doctor"
          />
          <CardContent>
            <List>
              {visit.prescriptions.map((rx, idx) => (
                <ListItem key={idx} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <MedIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography fontWeight={700}>{rx.medication}</Typography>}
                    secondary={rx.sig || 'Take as directed.'}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Medications Card (plain-language reminders) */}
      {visit.medication_reminders && visit.medication_reminders.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<MedIcon color="primary" />}
            title="Medication Reminders"
            titleTypographyProps={{ fontWeight: 600 }}
            subheader="Plain-language tips"
          />
          <CardContent>
            <List>
              {visit.medication_reminders.map((med, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={med} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* SOAP Note */}
      {visit.soap && (visit.soap.subjective || visit.soap.objective || visit.soap.assessment) && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            avatar={<SoapIcon color="primary" />}
            title="Clinical note (SOAP)"
            titleTypographyProps={{ fontWeight: 600 }}
            subheader="Your doctor's structured visit note"
            action={
              <IconButton onClick={() => setSoapOpen((v) => !v)} className="no-print">
                {soapOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
            onClick={() => setSoapOpen((v) => !v)}
            sx={{ cursor: 'pointer' }}
          />
          <Collapse in={soapOpen} timeout="auto" unmountOnExit>
            <CardContent sx={{ pt: 0 }}>
              {[
                { label: 'Subjective', body: visit.soap.subjective, hint: "What you told your doctor — symptoms, history, how you've been feeling." },
                { label: 'Objective', body: visit.soap.objective, hint: 'What your doctor observed and measured during the visit.' },
                { label: 'Assessment', body: visit.soap.assessment, hint: "Your doctor's clinical thinking — what's going on." },
              ]
                .filter((s) => s.body && s.body.trim())
                .map((s) => (
                  <Box key={s.label} sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f766e', mb: 0.25 }}>
                      {s.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      {s.hint}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {s.body}
                    </Typography>
                  </Box>
                ))}
            </CardContent>
          </Collapse>
        </Card>
      )}

      <Box sx={{ mt: 4, textAlign: 'center' }} className="no-print">
        <Typography variant="body2" color="text.secondary">
          Questions about your visit? Contact your provider's office.
        </Typography>
      </Box>

      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Email visit summary</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'll send a plain-text summary of this visit (not the transcript) to the address below.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Recipient email"
            size="small"
            value={emailRecipient}
            onChange={(e) => setEmailRecipient(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEmailSend}
            disabled={sendingEmail || !emailRecipient}
          >
            {sendingEmail ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
